import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useBentoConfig = (userId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updateLayout = useMutation({
    mutationFn: async (layout: any[]) => {
      const { data, error } = await supabase
        .from('profile_2_configs')
        .upsert({
          user_id: userId,
          layout_config: layout,
          is_enabled: true,
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bento-config', userId] });
    },
    onError: (error) => {
      console.error('Error updating bento config:', error);
      toast({
        title: "Error",
        description: "Failed to update layout. Please try again.",
        variant: "destructive",
      });
    },
  });

  const enableBentoProfile = useMutation({
    mutationFn: async () => {
      // Default layout with all widgets
      const defaultLayout = [
        { i: 'profile-header', x: 0, y: 0, w: 2, h: 1, minW: 2, minH: 1 },
        { i: 'bio', x: 2, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
        { i: 'stats', x: 0, y: 1, w: 1, h: 1, minW: 1, minH: 1 },
        { i: 'social-links', x: 1, y: 1, w: 1, h: 1, minW: 1, minH: 1 },
        { i: 'banner', x: 2, y: 1, w: 1, h: 2, minW: 1, minH: 1 },
        { i: 'contact', x: 0, y: 2, w: 1, h: 1, minW: 1, minH: 1 },
        { i: 'achievements', x: 1, y: 2, w: 1, h: 1, minW: 1, minH: 1 },
        { i: 'gallery', x: 0, y: 3, w: 3, h: 2, minW: 2, minH: 2 },
      ];

      const { data, error } = await supabase
        .from('profile_2_configs')
        .upsert({
          user_id: userId,
          is_enabled: true,
          layout_config: defaultLayout,
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bento-config', userId] });
      toast({
        title: "Success",
        description: "Bento profile enabled!",
      });
    },
  });

  return {
    updateLayout: updateLayout.mutate,
    enableBentoProfile: enableBentoProfile.mutate,
    isUpdating: updateLayout.isPending,
    isEnabling: enableBentoProfile.isPending,
  };
};